if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        this.form.addEventListener('submit', (evt) => {
          if (this.purchaseBlocked()) {
            evt.preventDefault();
            evt.stopImmediatePropagation();
          }
        }, true);

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    purchaseBlocked() {
      const section = this.closest('.js-quickbuy, .cc-featured-product, .shopify-section');
      return this.submitInFlight || this.dataset.addUncertain === 'true'
        || Boolean(section?.dataset.variantState);
    }

    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;
      evt.preventDefault();
      this.submitBtn = this.querySelector('[name="add"]');
      if (!this.submitBtn || this.submitBtn.disabled || this.purchaseBlocked()
        || this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      const submitBtn = this.submitBtn;
      this.submitInFlight = true;
      clearTimeout(this.successTimeout);
      submitBtn.classList.remove('is-success');
      if (theme.settings.vibrateOnATC && window.navigator.vibrate) window.navigator.vibrate(100);
      this.errorMsg = null;
      this.setErrorMsgState();
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('is-loading');
      this.form.setAttribute('aria-busy', 'true');

      const clickedSection = this.closest('.js-quickbuy, .cc-featured-product, .shopify-section');
      const clickedGeneration = clickedSection?.variantRequestId || 0;
      const clickedForm = this.form;
      const formData = new FormData(this.form);
      const requestedVariant = String(formData.get('id') || '');
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }
      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);
      const fetchRequestOpts = {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      };
      let outcome = 'not-sent';
      let addConfirmed = false;
      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(theme.strings.refinementAddError || theme.strings.cartError);
        const oldCartData = await oldCartResponse.json();
        if (!Array.isArray(oldCartData.items)) throw new Error('Invalid cart response');
        const idInput = this.form.querySelector('[name="id"]');
        const section = this.closest('.js-quickbuy, .cc-featured-product, .shopify-section');
        if (!this.isConnected || this.form !== clickedForm || section !== clickedSection
          || (section?.variantRequestId || 0) !== clickedGeneration
          || section?.dataset.variantState || !idInput || idInput.disabled
          || String(idInput.value) !== requestedVariant) {
          throw new Error(theme.strings.refinementAddError || theme.strings.cartError);
        }

        outcome = 'uncertain';
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.values(data.errors).map((value) => (Array.isArray(value) ? value.join(', ') : String(value)));
        }
        if (!response.ok || data.status) {
          if (response.status >= 400 && response.status < 500) outcome = 'rejected';
          this.setErrorMsgState(error || theme.strings.refinementAddError || theme.strings.cartError);
          throw new Error(theme.strings.refinementAddError || theme.strings.cartError);
        }
        if (!(data.variant_id || data.id) || (!Number.isFinite(Number(data.quantity)) || Number(data.quantity) < 1)) {
          throw new Error(theme.strings.refinementAddUncertain || theme.strings.cartError);
        }
        outcome = 'confirmed';
        addConfirmed = true;
        const variantId = data.variant_id || data.id;
        const itemInOldCart = oldCartData.items.find((item) => Number(item.variant_id) === Number(variantId));
        this.dispatchEvent(new CustomEvent(itemInOldCart ? 'on:line-item:change' : 'on:cart:add', {
          bubbles: true,
          detail: itemInOldCart
            ? { variantId, oldQuantity: itemInOldCart.quantity, newQuantity: data.quantity }
            : { variantId }
        }));

        if (theme.settings.afterAtc === 'page') {
          setTimeout(() => { window.location.href = theme.routes.cart; }, 300);
        } else {
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();
          try {
            ProductForm.updateCartIcon(data);
            if (this.cartDrawer) {
              await this.cartDrawer.renderContents(data, !quickAddDrawer && theme.settings.afterAtc === 'drawer');
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems?.dataset.empty === 'true') window.location.reload();
              else if (cartItems) await cartItems.refresh();
            }
          } catch (renderError) {
            this.setErrorMsgState(theme.strings.refinementCartRefreshError || theme.strings.cartError);
            this.appendCartLink();
          }
        }
      } catch (error) {
        if (outcome === 'uncertain') {
          this.dataset.addUncertain = 'true';
          this.querySelectorAll('.shopify-payment-button, .product-info__dynamic-checkout-button').forEach((control) => {
            control.inert = true;
            control.setAttribute('aria-disabled', 'true');
          });
          this.setErrorMsgState(theme.strings.refinementAddUncertain || theme.strings.cartError);
          this.appendCartLink();
        } else if (!this.errorMsg || this.errorMsg.hidden) {
          this.setErrorMsgState(theme.strings.refinementAddError || theme.strings.cartError);
        }
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: this.errorMsg?.textContent, uncertain: outcome === 'uncertain' }
        }));
      } finally {
        this.submitInFlight = false;
        this.form.removeAttribute('aria-busy');
        submitBtn.classList.remove('is-loading');
        const section = this.closest('.js-quickbuy, .cc-featured-product, .shopify-section');
        if (!section?.dataset.variantState && this.dataset.addUncertain !== 'true') {
          submitBtn.removeAttribute('aria-disabled');
        }
        if (this.dataset.addUncertain === 'true') submitBtn.disabled = true;
        if (addConfirmed) {
          submitBtn.classList.add('is-success');
          this.successTimeout = setTimeout(() => submitBtn.classList.remove('is-success'), 1400);
        }
      }
    }

    appendCartLink() {
      if (!this.errorMsg) return;
      const link = document.createElement('a');
      link.href = theme.routes.cart;
      link.className = 'link block mt-2';
      link.textContent = theme.strings.refinementViewCart || theme.strings.viewDetails;
      this.errorMsg.appendChild(link);
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        if (typeof response.sections?.['cart-icon-bubble'] !== 'string') throw new Error('Missing cart section');
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
